import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useCall, useDealerLeads, useDealerMe, useDealerOrders } from "@/api/commerce";
import { useMe } from "@/api/hooks";
import { StatusBadge } from "@/components/crm/Badge";
import { PoweredBy } from "@/components/PoweredBy";
import { Alert, Avatar, Spinner } from "@/components/ui";
import { useAuth } from "@/features/auth/AuthProvider";
import { OrderStatusBadge } from "@/features/orders/badges";
import { Progress } from "@/features/orders/OrdersPage";
import { BRAND } from "@/lib/brand";
import { money, shortDate } from "@/lib/format";

const blank = { company: "", contact_name: "", phone: "", email: "", city: "", state: "", requirement: "", expected_value: "", expected_close_date: "" };

function LeadForm() {
  const call = useCall("dealer-leads", "dealer-me");
  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);
  const field = (k: keyof typeof blank, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <div><label htmlFor={`lead-${k}`} className="label">{label}</label>
      <input id={`lead-${k}`} className="input" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} {...props} />
      {errors[k] && <p className="field-error">{errors[k]}</p>}</div>
  );
  const submit = async () => {
    setErrors({}); setMessage(null);
    const body: Record<string, unknown> = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim() || null]));
    try {
      await call.mutateAsync({ path: "/dealer/leads", body });
      setForm(blank);
      setMessage({ tone: "ok", text: "Thank you. Your channel manager will follow up and you can track it below." });
    } catch (e) {
      if (e instanceof ApiError && e.problem.errors?.length) setErrors(e.fieldErrors());
      else setMessage({ tone: "danger", text: e instanceof Error ? e.message : "Could not register the lead" });
    }
  };
  return (
    <section className="card space-y-4 p-5" aria-label="Register a lead">
      <h2 className="text-lg font-semibold">Register a lead</h2>
      {message && <Alert tone={message.tone}>{message.text}</Alert>}
      <form className="grid gap-3 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        {field("company", "Customer company *")}
        {field("contact_name", "Contact person *")}
        {field("phone", "Phone", { inputMode: "tel" })}
        {field("email", "Email", { type: "email" })}
        {field("city", "City")}
        {field("state", "State")}
        <div className="sm:col-span-2"><label htmlFor="lead-requirement" className="label">What do they need? *</label>
          <textarea id="lead-requirement" className="input min-h-20" value={form.requirement} onChange={(e) => setForm({ ...form, requirement: e.target.value })} />
          {errors.requirement && <p className="field-error">{errors.requirement}</p>}</div>
        {field("expected_value", "Expected value", { type: "number", min: 0 })}
        {field("expected_close_date", "Expected by", { type: "date" })}
        <div className="flex justify-end sm:col-span-2"><button type="submit" className="btn-primary" disabled={call.isPending}>Register lead</button></div>
      </form>
    </section>
  );
}

export function DealerPortal() {
  const me = useMe().data;
  const portal = useDealerMe();
  const leads = useDealerLeads();
  const orders = useDealerOrders();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const currency = me?.tenant.currency ?? "INR";
  return (
    <div className="min-h-full bg-canvas">
      <header className="flex items-center gap-3 border-b border-line bg-surface px-4 py-3 sm:px-6">
        <img src={BRAND.icon} alt="" className="h-7 w-7" />
        <div className="min-w-0 flex-1"><p className="truncate font-semibold">{me?.tenant.name} · Dealer portal</p><p className="truncate text-xs text-muted">{portal.data?.dealer.name}</p></div>
        {me && <Avatar name={me.user.full_name} />}
        <button type="button" className="btn-ghost" onClick={() => void logout().then(() => navigate("/login"))}>Sign out</button>
      </header>
      <main className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
        {portal.error && <Alert>{portal.error.message}</Alert>}
        {portal.isLoading && <Spinner />}
        {portal.data && (
          <div className="grid gap-4 sm:grid-cols-4">
            <div className="card p-4"><p className="text-xs uppercase tracking-wide text-muted">Open leads</p><p className="mt-1 text-2xl font-semibold">{portal.data.open_leads}</p></div>
            <div className="card p-4"><p className="text-xs uppercase tracking-wide text-muted">Won</p><p className="mt-1 text-2xl font-semibold text-ok">{portal.data.won_leads}</p></div>
            <div className="card p-4"><p className="text-xs uppercase tracking-wide text-muted">Open orders</p><p className="mt-1 text-2xl font-semibold">{portal.data.open_orders}</p></div>
            <div className="card p-4"><p className="text-xs uppercase tracking-wide text-muted">Your contact</p><p className="mt-1 font-semibold">{portal.data.channel_manager ?? "Sales team"}</p>
              {portal.data.channel_manager_email && <a className="text-xs text-brand hover:underline" href={`mailto:${portal.data.channel_manager_email}`}>{portal.data.channel_manager_email}</a>}</div>
          </div>
        )}
        <div className="grid gap-6 lg:grid-cols-2">
          <LeadForm />
          <section className="card" aria-label="Your leads">
            <h2 className="border-b border-line px-5 py-3 text-lg font-semibold">Your leads</h2>
            {leads.data?.length ? (
              <ul className="divide-y divide-line">
                {leads.data.map((l) => (
                  <li key={l.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                    <span className="min-w-0"><span className="block truncate font-medium">{l.customer ?? l.name}</span><span className="text-xs text-muted">{l.stage} · {shortDate(l.created_at)}{l.owner && ` · ${l.owner}`}</span></span>
                    <span className="flex shrink-0 items-center gap-2">{l.amount > 0 && money(l.amount, l.currency)}<StatusBadge status={l.status} /></span>
                  </li>
                ))}
              </ul>
            ) : <p className="px-5 py-4 text-sm text-muted">Leads you register appear here with their progress.</p>}
          </section>
        </div>
        <section className="card overflow-x-auto" aria-label="Orders">
          <h2 className="border-b border-line px-5 py-3 text-lg font-semibold">Orders</h2>
          {orders.data?.length ? (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-muted"><tr><th className="px-5 py-2">Order</th><th className="px-2 py-2">Customer</th><th className="px-2 py-2 text-right">Value</th><th className="px-2 py-2">Delivered</th><th className="px-5 py-2">Status</th></tr></thead>
              <tbody>
                {orders.data.map((o) => (
                  <tr key={o.id} className="border-t border-line">
                    <td className="px-5 py-2.5"><span className="font-medium">{o.number}</span><span className="block text-xs text-muted">{shortDate(o.order_date)}</span></td>
                    <td className="px-2 py-2.5">{o.customer}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{money(o.grand_total, o.currency || currency)}</td>
                    <td className="px-2 py-2.5"><Progress pct={o.delivered_pct} /></td>
                    <td className="px-5 py-2.5"><OrderStatusBadge status={o.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="px-5 py-4 text-sm text-muted">Orders for you or your customers appear here.</p>}
        </section>
        <PoweredBy className="justify-center" />
      </main>
    </div>
  );
}
